import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import {
  BATCH_UPLOAD_CONCURRENCY,
  type MediaHashCheckResponse,
  type MediaUploadResponse,
} from "@ckgo2/shared";
import { api, uploadFormData, type ApiResponse } from "@/api/client";
import type { BatchUpload, BrowserCaps } from "./batchUpload.types";
import { createBatchController, createBatchResources, type BatchDeps } from "./batchUploadModel";
import { decideBrowserPrep, detectBrowserCaps, prepareForUpload } from "./browserPrep";
import { sha256File } from "./fileHash";
import { uploadTypeOf } from "./fileTypes";

/**
 * Prostředky souběhu jedné karty: řadič vzniká při každém připojení hostitele, ale nahrání
 * odpojeného řadiče dál letí, takže strop `BATCH_UPLOAD_CONCURRENCY` drží sada společná všem.
 */
const TAB_RESOURCES = createBatchResources();

/** Skutečné závislosti řadiče: `api` klient, příprava v prohlížeči a otisk. */
function createDeps(): BatchDeps {
  // Schopnosti prohlížeče jednou za řadič; selhaná detekce se při dalším souboru zkusí znovu.
  let caps: Promise<BrowserCaps> | null = null;
  const browserCaps = () =>
    (caps ??= detectBrowserCaps().catch((err: unknown) => {
      caps = null;
      throw err;
    }));

  return {
    hashFile: sha256File,
    async checkHashes(hashes) {
      // Doslovná cesta: na ní stojí měřidlo čtenářů tras (AK38).
      const res = await api.post<ApiResponse<MediaHashCheckResponse>>(
        "/api/admin/upload/check-hashes",
        { hashes },
      );
      return res.data.matches;
    },
    async precheck(file) {
      const decision = decideBrowserPrep(
        { type: uploadTypeOf(file), name: file.name, size: file.size },
        await browserCaps(),
      );
      return decision.kind === "permanent" ? decision.reason : null;
    },
    async prepare(file, signal) {
      return prepareForUpload(file, await browserCaps(), signal);
    },
    // Výpadek sítě převádí `uploadFormData` na `ApiError` se `status` 0 (opakovatelná chyba).
    upload: async (form) =>
      (await uploadFormData<ApiResponse<MediaUploadResponse>>("/api/admin/upload", form)).data,
    adopt: (body) => api.post("/api/admin/upload/bulk", body),
    restore: (id) => api.post(`/api/admin/upload/${id}/restore`, {}),
    now: Date.now,
    wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    concurrency: BATCH_UPLOAD_CONCURRENCY,
  };
}

/**
 * Model dávky nahrávání (souběh, otisky, kontrola duplicit, příprava, nahrání, opakování).
 * Sémantika rozhraní: `batchUpload.types.ts`; logika v `batchUploadModel.ts`. Hostitel
 * odvozuje výběr i obnovu seznamu ze stavu souborů (`batchUploadHost.ts`), zpětné volání
 * po nahrání proto hák nemá.
 *
 * Řadič vzniká jednou v línném inicializátoru. Odpojení hostitele (stránka opuštěná po
 * „Odejít“, zavřené okno výběru) dávku vynuluje: čekající soubory neodejdou (dialog odchodu to
 * slibuje), letící požadavky doběhnou na serveru (`uploadFormData` neumí `AbortSignal`) a místo
 * souběhu drží v `TAB_RESOURCES`, dokud neodpoví (nový řadič za ně nepošle další).
 * StrictMode úklid při prvním připojení nuluje prázdnou dávku, řadič zůstává použitelný.
 * Hák neinvaliduje dotazy React Query; to dělají panel a hostitelé.
 */
export function useBatchUpload(): BatchUpload {
  const [controller] = useState(() => createBatchController(createDeps(), TAB_RESOURCES));
  useEffect(() => () => controller.reset(), [controller]);

  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);

  return useMemo(
    () => ({
      ...snapshot,
      add: controller.add,
      remove: controller.remove,
      start: controller.start,
      retry: controller.retry,
      retryAllFailed: controller.retryAllFailed,
      restoreFromTrash: controller.restoreFromTrash,
      reset: controller.reset,
    }),
    [snapshot, controller],
  );
}
